import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Edit, MoreHorizontal, Trash2 } from 'lucide-react'

export interface ZeilenEintrag {
  label: string
  icon?: React.ReactNode
  onClick: () => void
}

/** Zeilenaktionen einer Admin-Tabelle: ein „⋯“-Menü (Bearbeiten, weitere Einträge, Löschen abgesetzt).
 *  Klicks hier lösen nie den Zeilenklick aus; die Lösch-Bestätigung bleibt beim Aufrufer. */
export function ZeilenAktionen({ name, onBearbeiten, onLoeschen, eintraege }: {
  name: string
  onBearbeiten?: () => void
  onLoeschen?: () => void
  eintraege?: ZeilenEintrag[]
}) {
  const [pos, setPos] = useState<{ top: number; right: number; oben: boolean } | null>(null)
  const knopf = useRef<HTMLButtonElement>(null)
  const menue = useRef<HTMLDivElement>(null)
  const offen = pos !== null

  useEffect(() => {
    if (!offen) return
    const zu = () => setPos(null)
    const aussen = (e: MouseEvent) => {
      const t = e.target as Node
      if (menue.current?.contains(t) || knopf.current?.contains(t)) return
      zu()
    }
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') { zu(); knopf.current?.focus() } }
    document.addEventListener('mousedown', aussen)
    document.addEventListener('keydown', taste)
    window.addEventListener('resize', zu)
    window.addEventListener('scroll', zu, true)
    return () => {
      document.removeEventListener('mousedown', aussen)
      document.removeEventListener('keydown', taste)
      window.removeEventListener('resize', zu)
      window.removeEventListener('scroll', zu, true)
    }
  }, [offen])

  useEffect(() => { if (offen) menue.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus() }, [offen])

  function umschalten() {
    if (offen) { setPos(null); return }
    const r = knopf.current!.getBoundingClientRect()
    const hoehe = 56 + 44 * ((eintraege?.length ?? 0) + (onBearbeiten ? 1 : 0) + (onLoeschen ? 1 : 0))
    const oben = r.bottom + hoehe > window.innerHeight && r.top > hoehe
    setPos({ top: oben ? r.top - 4 : r.bottom + 4, right: Math.max(8, window.innerWidth - r.right), oben })
  }

  const alle: ZeilenEintrag[] = [
    ...(onBearbeiten ? [{ label: 'Bearbeiten', icon: <Edit className="h-4 w-4" />, onClick: onBearbeiten }] : []),
    ...(eintraege ?? []),
  ]
  const eintrag = 'flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm focus:outline-none'

  return (
    <div className="flex items-center" data-no-row-click>
      <button ref={knopf} type="button" onClick={umschalten} title={`Aktionen für ${name}`} aria-label={`Aktionen für ${name}`}
        aria-haspopup="menu" aria-expanded={offen}
        className="flex h-11 w-11 items-center justify-center rounded-lg text-dark-text-muted transition-colors hover:bg-dark-bg-tertiary hover:text-dark-text focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400">
        <MoreHorizontal className="h-5 w-5" />
      </button>
      {offen && createPortal(
        <div ref={menue} role="menu" aria-label={`Aktionen für ${name}`}
          onClick={e => e.stopPropagation()} onKeyDown={e => {
            e.stopPropagation()
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault()
              const l = Array.from(menue.current!.querySelectorAll<HTMLElement>('[role="menuitem"]'))
              const i = l.indexOf(document.activeElement as HTMLElement)
              l[(i + (e.key === 'ArrowDown' ? 1 : -1) + l.length) % l.length]?.focus()
            }
          }}
          style={{ position: 'fixed', right: pos!.right, ...(pos!.oben ? { bottom: window.innerHeight - pos!.top } : { top: pos!.top }) }}
          className="z-[60] min-w-[200px] overflow-hidden rounded-xl border border-dark-border bg-dark-bg-secondary py-1 shadow-xl">
          {alle.map(e => (
            <button key={e.label} type="button" role="menuitem"
              onClick={() => { setPos(null); e.onClick() }}
              className={`${eintrag} text-dark-text hover:bg-dark-bg-tertiary focus:bg-dark-bg-tertiary`}>
              {e.icon}{e.label}
            </button>
          ))}
          {onLoeschen && (
            <>
              {alle.length > 0 && <div aria-hidden="true" className="my-1 h-px bg-dark-border" />}
              <button type="button" role="menuitem" onClick={() => { setPos(null); onLoeschen() }}
                className={`${eintrag} text-red-400 hover:bg-red-500/10 focus:bg-red-500/10`}>
                <Trash2 className="h-4 w-4" />Löschen
              </button>
            </>
          )}
        </div>, document.body)}
    </div>
  )
}
